import { Box } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useGetMainComponentTypeList } from 'src/api/main-component-type'
import { invalidateMaintenanceTemplateCache, useAddMaintenanceTemplate } from 'src/api/maintenanceTemplate'
import { AppForm, FormSelectField, FormTextField } from 'src/components/FormCore'
import { GenericDialogLayout } from 'src/components/GenericDialogLayout'
import { MainButton } from 'src/components/MainButton'
import { MaintenanceTemplateType } from 'src/enums'
import { IMainComponentType } from 'src/generated-types/main-component-type'
import { ILocalityEquipmentMaintenance } from 'src/generated-types/maintenance'
import { useZodForm } from 'src/hooks/zod-form'
import { ShowDialogProps } from 'src/show-dialog'
import { Flex } from 'src/styles/flexComponent'
import { z } from 'zod'
import MaintenanceTemplatesMaintenanceContainer from './MaintenanceTemplatesMaintenanceContainer'
import { getTranslatedText } from 'src/utils'
import { useDialogCtx } from 'src/hooks/context-hooks'

interface CreateMaintenanceTemplateProps extends ShowDialogProps {
  customerId: EntityId
  type: MaintenanceTemplateType
}
const CreateMaintenanceTemplateForm: React.FC<CreateMaintenanceTemplateProps> = ({ pop, customerId, type }) => {
  const { mutateAsync, isLoading } = useAddMaintenanceTemplate(customerId)
  const { t: translate } = useTranslation(['maintenance', 'common', 'common-api-message'])
  const [maintenances, setMaintenances] = useState<ILocalityEquipmentMaintenance[]>([])
  const [hasContent, setHasContent] = useState(false)
  const { showConfirmDialog } = useDialogCtx()

  const mainComponentTypes = useGetMainComponentTypeList(customerId as EntityId)
  const genericSchema = z.object({
    id: z.number(),
  })
  const genericMaintenanceSchema = z.object({
    localityId: z.number(),
  })

  const maintenanceTemplateSchema = z.object({
    name: z.string().min(1).max(200),
    description: z.string().min(1).max(400),
    type: z.number().default(type),
    mainComponentTypeMaintenanceTemplates: z.array(genericSchema).min(1).nullable().optional(),
    maintenances: z.array(genericMaintenanceSchema).nullable().optional(),
  })
  const form = useZodForm(maintenanceTemplateSchema)

  useEffect(() => {
    const hasFormContent = form.formState.isDirty
    const hasMaintenanceContent = maintenances.length > 0

    setHasContent(hasFormContent || hasMaintenanceContent)
  }, [form.watch(), maintenances, type])

  const handleClose = useCallback(() => {
    if (hasContent) {
      showConfirmDialog(
        translate('confirm-discard-changes', { ns: 'common' }),
        translate('unsaved-maintenance-changes-warning', { ns: 'maintenance' }),
        {
          acceptText: translate('discard-changes', { ns: 'common' }),
          cancelText: translate('cancel', { ns: 'common' }),
        },
        () => pop(),
      )
    } else {
      pop()
    }
  }, [hasContent, translate])

  const onSubmit = async form => {
    if (maintenances.length == 0) {
      toast.warning(translate('maintenance-template-requires-maintenances', { ns: 'maintenance' }))
      return
    }

    if (type == MaintenanceTemplateType.Equipment) {
      form.mainComponentTypeMaintenanceTemplates = form.mainComponentTypeMaintenanceTemplates.map(m => ({
        mainComponentTypeId: m.id,
      }))
    }
    form.maintenances = maintenances
    await mutateAsync(form, {
      onSuccess: () => {
        if (type == MaintenanceTemplateType.Equipment)
          invalidateMaintenanceTemplateCache.getEquipmentMaintenanceTemplates(customerId as EntityId)
        if (type == MaintenanceTemplateType.Locality)
          invalidateMaintenanceTemplateCache.getLocalityMaintenanceTemplates(customerId as EntityId)
        toast.success(translate('maintenance-template-added-success', { ns: 'maintenance' }))
        pop()
      },
      onError: () => {
        toast.error(translate('unExpectedError', { ns: 'common-api-message' }))
      },
    })
  }

  return (
    <div>
      <GenericDialogLayout
        title={
          type === MaintenanceTemplateType.Equipment
            ? translate('add-equipment-maintenance-template', { ns: 'maintenance' })
            : translate('add-locality-maintenance-template', { ns: 'maintenance' })
        }
        pop={handleClose}
        removedCloseButton={true}
        actions={[
          hasContent && (
            <MainButton variant="outlined" color="primary" key="discard-button" onClick={handleClose}>
              {translate('discard-changes', { ns: 'common' })}
            </MainButton>
          ),
          <MainButton
            variant="contained"
            key="save-button"
            onClick={() => {
              form.handleSubmit(onSubmit)()
            }}
            disabled={!hasContent}
            loading={isLoading}
          >
            {translate('save-&-exit', { ns: 'common' })}
          </MainButton>,
        ].filter(Boolean)}
      >
        <Box>
          <AppForm
            form={form}
            onSubmit={form => {
              onSubmit(form)
            }}
          >
            <Flex.Row>
              <Flex.Column width={type === MaintenanceTemplateType.Locality ? '100%' : '60%'}>
                <FormTextField name="name" label={translate('title', { ns: 'common' })} />
              </Flex.Column>
              {type === MaintenanceTemplateType.Equipment && (
                <Flex.Column width={'40%'}>
                  <FormSelectField
                    name="mainComponentTypeMaintenanceTemplates"
                    data={((mainComponentTypes.data || []) as IMainComponentType[]) || []}
                    label={translate('type-of-equipment', { ns: 'maintenance' })}
                    getOptionLabel={option => getTranslatedText(option.name)}
                    renderOption={(props: any, option) => (
                      <Box {...props} key={option.id}>
                        {getTranslatedText(option.name)}
                      </Box>
                    )}
                    multiple
                    defaultValue={[]}
                    clearOnBlur={false}
                    clearOnEscape={false}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                  />
                </Flex.Column>
              )}
            </Flex.Row>
            <FormTextField name="description" label={translate('description', { ns: 'common' })} />
          </AppForm>
        </Box>
        <Box>
          <MaintenanceTemplatesMaintenanceContainer maintenances={maintenances} setMaintenances={setMaintenances} />
        </Box>
      </GenericDialogLayout>
    </div>
  )
}

export default CreateMaintenanceTemplateForm
